import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { OpenInNew, ArrowBack } from "@mui/icons-material";
import axios from "axios";
import ChatBubble from "components/aiChat/ChatBubble";
import MarkdownViewer from "components/aiChat/MarkdownViewer";
import { AiMessage } from "hooks/useLLM";
import { getTranslation } from "common";
import { buildSharedApiUrl, buildSharedApiUrlWithHelpers } from "common/shareUrl";
import { useTranslations, useUser } from "hooks";
import Logo from "image/Logo.svg";

/**
 * Backend payload shape for a shared chat session. The route
 * exposed to the user on the site is `/ai/shared/:slug`; the SPA
 * fetches the payload from the public BE endpoint
 * `${BE_ORIGIN}/ai/shared/<slug>` (built by `buildSharedApiUrl`,
 * which strips the trailing `/helpers` from
 * `REACT_APP_STATS_BACKEND_BASE_URI`). The BE responds with
 * `{ data: SharedSessionPayload }` — the same wrapper convention
 * used elsewhere in the AI API.
 */
type SharedSessionPayload = {
  id_session: number;
  context: string;
  model: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  first_question: string;
  title: string;
  shared: boolean;
  share_hash: string;
  messages: Array<{
    id: number;
    question: string;
    answer: string;
    model: string;
    context: string;
    created_at: string;
  }>;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: SharedSessionPayload; messages: AiMessage[] }
  | { kind: "not_found" }
  | { kind: "error"; message: string };

/**
 * Format a backend ISO timestamp into a short locale-aware string
 * suitable for the page header. Returns an empty string on parse
 * failure so we never break the layout.
 */
const formatTimestamp = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
};

const SharedChatPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const translations = useTranslations();
  const user = useUser();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  // The shared chat endpoint on the BE is
  // `${BE_ORIGIN}/ai/shared/<slug>` (where `BE_ORIGIN` is
  // `REACT_APP_STATS_BACKEND_BASE_URI` with the trailing `/helpers`
  // stripped — see `buildSharedApiUrl`). The SPA exposes the same
  // content under /#/ai/shared/<slug> and fetches this URL
  // internally — we never expose the BE URL to the user.
  // `buildSharedApiUrl` (defined in `common/shareUrl.ts`) handles
  // both the absolute and the relative-base cases. Some prod-like
  // deployments expose the public endpoint under `/helpers/ai/shared/`
  // instead of `/ai/shared/`, so we keep a fallback URL ready.
  const sharedUrl = React.useMemo(() => buildSharedApiUrl(slug || ""), [slug]);
  const sharedUrlWithHelpers = React.useMemo(
    () => buildSharedApiUrlWithHelpers(slug || ""),
    [slug]
  );

  useEffect(() => {
    if (!slug) {
      setState({ kind: "not_found" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });

    const fetchShared = (url: string, fallbackUrl?: string) => {
      axios
        .get<{ data: SharedSessionPayload }>(url, {
          headers: { "Accept-Language": navigator.language.split("-")[0] },
          // No JWT, no interceptors: the route is public.
          // The backend decides validity via the slug alone.
        })
        .then((res) => {
          if (cancelled) return;
          const payload = res.data?.data;
          if (!payload || !Array.isArray(payload.messages)) {
            setState({ kind: "not_found" });
            return;
          }
          // Map backend {question, answer} pairs to the AiMessage shape
          // ChatBubble / MarkdownViewer expect. We derive a stable uuid
          // so React keys are unique even when the user navigates back
          // and forth.
          const messages: AiMessage[] = payload.messages.flatMap((m, idx) => {
            const out: AiMessage[] = [];
            if (m.question) {
              out.push({
                uuid: `${payload.id_session}-${m.id ?? idx}-q`,
                isAi: false,
                message: m.question,
                isLoading: false,
              });
            }
            if (m.answer) {
              out.push({
                uuid: `${payload.id_session}-${m.id ?? idx}-a`,
                isAi: true,
                message: m.answer,
                isLoading: false,
              });
            }
            return out;
          });
          setState({ kind: "ok", data: payload, messages });
          // Friendly document.title for browser tabs and link previews.
          document.title = `Flowenti AI — ${payload.title || "Chat condivisa"}`;
        })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          // First attempt 404'd and we have a different fallback URL?
          // Retry there before giving up.
          if (status === 404 && fallbackUrl && url !== fallbackUrl) {
            fetchShared(fallbackUrl);
            return;
          }
          if (status === 404 || status === 403) {
            setState({ kind: "not_found" });
            return;
          }
          setState({
            kind: "error",
            message:
              err?.response?.data?.message ||
              err?.message ||
              "Impossibile recuperare la chat condivisa.",
          });
        });
    };

    fetchShared(sharedUrl, sharedUrlWithHelpers);

    return () => {
      cancelled = true;
    };
  }, [sharedUrl, sharedUrlWithHelpers, slug]);

  // ------- Render -------
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : "#f5f5f7",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Minimal header — full Navbar would be overkill for a
          single-conversation page. The Flowenti logo and a "open the
          app" CTA is enough to anchor the user. */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "rgba(255, 255, 255, 0.95)",
          borderBottom: "1px solid",
          borderColor: "divider",
          backdropFilter: "blur(12px)",
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ textDecoration: "none", color: "inherit" }}
          >
            <img src={Logo} alt="Flowenti" style={{ height: 36 }} />
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              Flowenti
            </Typography>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <Button
            component={RouterLink}
            to="/"
            size="small"
            startIcon={<ArrowBack fontSize="small" />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              color: "text.secondary",
            }}
          >
            {getTranslation(translations, "generic.home") || "Home"}
          </Button>
        </Toolbar>
      </AppBar>

      <Container
        maxWidth="md"
        component="main"
        sx={{
          flex: 1,
          py: { xs: 3, md: 5 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {state.kind === "loading" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              {getTranslation(translations, "ai_drawer.loading") ||
                "Caricamento..."}
            </Typography>
          </Box>
        )}

        {state.kind === "not_found" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
              textAlign: "center",
            }}
          >
            <Alert severity="warning" sx={{ maxWidth: 480 }}>
              {getTranslation(
                translations,
                "shared_chat.not_found"
              ) ||
                "Questa chat condivisa non è più disponibile o il link non è valido."}
            </Alert>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              sx={{ borderRadius: 999, textTransform: "none" }}
            >
              {getTranslation(translations, "generic.home") || "Torna alla home"}
            </Button>
          </Box>
        )}

        {state.kind === "error" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
              textAlign: "center",
            }}
          >
            <Alert severity="error" sx={{ maxWidth: 480 }}>
              {state.message}
            </Alert>
            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{ borderRadius: 999, textTransform: "none" }}
            >
              {getTranslation(translations, "generic.retry") || "Riprova"}
            </Button>
          </Box>
        )}

        {state.kind === "ok" && (
          <>
            {/* Optional banner when the visitor is authenticated —
                they might be confused about why a "public" link is
                showing them content from their own org. We surface
                the existing user info to make the context explicit. */}
            {user?.userId && (
              <Alert
                severity="info"
                sx={{ mb: 2, borderRadius: 2 }}
                icon={<OpenInNew fontSize="small" />}
              >
                {getTranslation(
                  translations,
                  "shared_chat.viewing_as_visitor"
                ) ||
                  "Stai visualizzando una chat condivisa pubblicamente. La tua sessione di login non è stata usata per generare questo link."}
              </Alert>
            )}

            {/* Page header: title + meta (model, date, count). */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}
              >
                {state.data.title ||
                  state.data.first_question ||
                  "Chat condivisa"}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  {formatTimestamp(state.data.created_at)}
                  {state.data.message_count > 0 &&
                    ` · ${state.data.message_count} ${
                      state.data.message_count === 1
                        ? "messaggio"
                        : "messaggi"
                    }`}
                </Typography>
              </Stack>
              <Divider sx={{ mt: 2 }} />
            </Box>

            {/* Chat thread — reuses the same ChatBubble / MarkdownViewer
                primitives the AiDrawer uses, so the visual language
                stays consistent. No input, no actions: read-only. */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                pb: 4,
              }}
            >
              {state.messages.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", mt: 4 }}
                >
                  {getTranslation(
                    translations,
                    "shared_chat.empty"
                  ) || "Nessun messaggio in questa chat."}
                </Typography>
              ) : (
                state.messages.map((m) => (
                  <ChatBubble key={m.uuid} isAi={m.isAi}>
                    {m.isAi ? (
                      <MarkdownViewer
                        uuid={m.uuid}
                        message={m.message}
                        // No `handleApplyAi` — the share viewer has
                        // no "apply to modeler" action.
                      />
                    ) : (
                      <Typography sx={{ whiteSpace: "pre-wrap" }}>
                        {m.message}
                      </Typography>
                    )}
                  </ChatBubble>
                ))
              )}
            </Box>
          </>
        )}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: "center",
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "#f8f9fa",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {getTranslation(translations, "shared_chat.footer") ||
            "Condivisa tramite Flowenti AI · "}
          {/* <RouterLink
            to="/"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            {getTranslation(translations, "shared_chat.footer_cta") ||
              "crea la tua"}
          </RouterLink> */}
        </Typography>
      </Box>
    </Box>
  );
};

export default SharedChatPage;
